import { FunctionComponent, useState } from 'react'
import styles from './groupLogo.module.scss'
import { useTranslation } from 'react-i18next'
import { DaycareGroup } from '@/types/DaycareGroup'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { useDropzone } from 'react-dropzone'
import { MediaType } from '@/types/NewsFeed'
import ConfirmDeleteGroupLogoDialog from './Dialog/confirmDeleteGroupLogo'
import { useMutation } from '@apollo/client'
import { ADD_DAYCARE_GROUP_LOGO } from '@/modules/API/daycareGroup/daycareGroupHandler'
import Loading from '@/components/Loading/Loading'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface GroupLogoProps {
    group?: DaycareGroup
    refetchGroups: () => void
    canWrite: boolean
}

const FILE_LIMIT: number = 1

const GroupLogo: FunctionComponent<GroupLogoProps> = ({ group, refetchGroups, canWrite }: GroupLogoProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [isConfirmDeleteLogoDialogOpen, setIsConfirmDeleteLogoDialogOpen] = useState<boolean>(false)

    const [loadingCreateLogo, setLoadingCreateLogo] = useState<boolean>(false)

    const [addDaycareGroupLogo] = useMutation(ADD_DAYCARE_GROUP_LOGO, {
        onCompleted: () => {
            showNotificationSuccess(t('addedLogo'))

            refetchGroups()
        },
        onError: () => {
            showNotificationError(t('logoAddedError'))
        }
    })

    async function onDrop(acceptedFiles: File[]): Promise<void> {
        setLoadingCreateLogo(true)

        if (acceptedFiles.length === 0) return

        await Promise.all(
            acceptedFiles.map(async (file) => {
                const fileType: MediaType | null = defineFileType(file.type)
                if (!fileType) {
                    showNotificationError(t('fileNotSupported'))
                    return
                }

                let width: number = 1600
                let height: number = 1200

                if (file.type.startsWith('image')) {
                    const image: HTMLImageElement = new Image()
                    image.src = URL.createObjectURL(file)
                    await new Promise<void>((resolve) => {
                        image.onload = () => {
                            width = image.width
                            height = image.height
                            resolve()
                        }
                    })
                }

                await addDaycareGroupLogo({
                    variables: {
                        groupId: group?.id,
                        file: file,
                        width: width,
                        height: height
                    }
                })
            })
        )

        setLoadingCreateLogo(false)
    }

    function defineFileType(type: string): MediaType | null {
        switch (type) {
            case 'image/jpeg':
            case 'image/jpg':
            case 'image/png':
                return MediaType.IMAGE
            default:
                return null
        }
    }

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'image/jpeg': [],
            'image/jpg': [],
            'image/png': []
        },
        maxFiles: FILE_LIMIT,
        onDrop
    })

    return (
        <div className={styles.Container}>
            <div className={styles.Title}>
                <p> {t('logo')} </p>
            </div>
            {group?.logoBase64Data ? (
                <>
                    <div className={styles.LogoContainer}>
                        <img
                            src={`data:image/png;base64,${group.logoBase64Data}`}
                            alt={`${group.name} logo`}
                            className={styles.Logo}
                        />
                    </div>
                    {canWrite && (
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'delete'}
                                onClick={() => setIsConfirmDeleteLogoDialogOpen(true)}
                                className={styles.Button}
                            />
                        </div>
                    )}
                    {isConfirmDeleteLogoDialogOpen && (
                        <ConfirmDeleteGroupLogoDialog
                            open={isConfirmDeleteLogoDialogOpen}
                            handleClose={() => setIsConfirmDeleteLogoDialogOpen(false)}
                            groupId={group.id}
                            refetchGroups={refetchGroups}
                        />
                    )}
                </>
            ) : (
                <>
                    {loadingCreateLogo && <Loading overall />}
                    <div className={styles.DropZoneContainer}>
                        <div {...getRootProps()} className={styles.DropZone}>
                            <FileUploadIcon className={styles.DropZoneImg} />
                            <input {...getInputProps()} />
                            <p className={styles.DropZoneText}>{t('dropZoneMessage')}</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default GroupLogo
